/**
 * Изменения сметы по запросам на изменение (СМ-12): запросы, которые касаются сметы, разница с прежней версией
 * по позициям, итог для бюджета (БМ-10) и допсоглашения по этим запросам. Показывается, когда к смете привязан
 * запрос; суммы — по уровню денег.
 */
import { CHANGE_STATUSES, type ChangeStatus, type EstimateChangeImpact } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { Badge } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

const linkCls = 'inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-0';
const AMENDMENT_STATUS: Record<string, string> = {
  draft: 'черновик',
  approval: 'на согласовании',
  approved: 'согласовано',
  signed: 'подписано',
  cancelled: 'отменено',
};
const ROW_STATUS: Record<string, string> = { added: 'добавлена', removed: 'убрана', changed: 'изменена' };

export function ChangeImpact({ estimateId, projectId }: { estimateId: string; projectId: string }) {
  const q = useQuery({
    queryKey: ['estimate', estimateId, 'change-impact'],
    queryFn: () => api<EstimateChangeImpact>(`/estimates/${estimateId}/change-impact`),
    retry: false,
  });
  const d = q.data;
  if (!d || (!d.changes.length && !d.version.reason)) return null;
  return (
    <Panel title="Изменения по запросам" className="lg:col-span-3">
      <div className="flex flex-col gap-4 text-[14px]">
        {d.version.reason ? (
          <p>
            Причина этой версии: {d.version.reason}
            {d.version.changeId && d.version.changeNumber ? (
              <>
                {' '}
                (запрос{' '}
                <Link to="/changes/$changeId" params={{ changeId: d.version.changeId }} className={linkCls}>
                  {d.version.changeNumber}
                </Link>
                )
              </>
            ) : null}
          </p>
        ) : null}
        <ul className="flex flex-col gap-1">
          {d.changes.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2">
              <Link to="/changes/$changeId" params={{ changeId: c.id }} className={linkCls}>
                {c.number}
              </Link>
              <span className="text-ink-2">{c.title}</span>
              <Badge tone="neutral">{CHANGE_STATUSES[c.status as ChangeStatus] ?? c.status}</Badge>
              {c.costDelta !== null && c.costDelta !== 0 ? (
                <span className="text-ink-2">себестоимость {tenge(c.costDelta)}</span>
              ) : null}
            </li>
          ))}
        </ul>
        {d.previous && d.diff ? (
          <div>
            <p className="font-medium">
              Против версии {d.previous.versionNo}: добавлено {d.diff.added}, убрано {d.diff.removed},
              изменено {d.diff.changed}
              {d.diff.delta !== null ? `, разница ${tenge(d.diff.delta)}` : ''}
            </p>
            {d.diff.rows.length ? (
              <ul className="mt-1 flex flex-col gap-1 text-[13px] text-ink-2">
                {d.diff.rows.slice(0, 10).map((r, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: строки могут повторяться
                  <li key={`${i}-${r.status}-${r.name}`}>
                    {ROW_STATUS[r.status] ?? r.status}: {r.name}
                    {r.delta !== null ? ` (${tenge(r.delta)})` : ''}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <p className="text-ink-2">Это первая версия сметы: сравнивать пока не с чем.</p>
        )}
        {d.budget ? (
          <p>
            Бюджет: утверждено изменений {d.budget.approvedCount}
            {d.budget.approvedDelta !== null ? ` на ${tenge(d.budget.approvedDelta)}` : ''}, ждёт решения{' '}
            {d.budget.proposedCount}.{' '}
            <Link to="/projects/$projectId/$tab" params={{ projectId, tab: 'budget' }} className={linkCls}>
              Бюджет и маржа
            </Link>
          </p>
        ) : null}
        {d.amendments.length ? (
          <p className="flex flex-wrap items-center gap-2">
            Допсоглашения:
            {d.amendments.map((a) => (
              <Link
                key={a.id}
                to="/contracts/$contractId"
                params={{ contractId: a.contractId }}
                className={linkCls}
              >
                {a.contractNumber}, № {a.number} ({AMENDMENT_STATUS[a.status] ?? a.status})
              </Link>
            ))}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
